import { NotificationStatus, RoleEnum, SendToAllEnum } from "@/types";
import z from "zod";

const getNotificationSchema = (role: RoleEnum) =>
  z
    .object({
      send_time: z
        .string({ message: "この項目は必須です。" })
        .min(1, "この項目は必須です。")
        .refine(
          (val) => {
            const minMs = new Date().getTime() + 60 * 60 * 1000; // + 1h in ms
            const valueMs = new Date(val).getTime();
            return valueMs >= minMs;
          },
          { message: "送信時間は現在時刻より1時間以上後を選択してください。" }
        ),
      title: z
        .string()
        .trim()
        .min(1, "この項目は必須です。")
        .max(150, { message: "通知テーマは150文字以下で入力してください。" }),
      description: z.string().trim().min(1, "この項目は必須です。").max(2000, {
        message: "通知内容は2000文字以下で入力してください。",
      }),
      manager: z
        .string({ message: "この項目は必須です。" })
        .trim()
        .min(1, "この項目は必須です。")
        .max(150, { message: "担当者は150文字以下で入力してください。" }),
      status: z.enum(NotificationStatus),
      stores: z.array(z.any()).optional(), // {value, label}
      groups: z.array(z.any()).optional(), // {value, label}
      storeUsers: z.array(z.any()).optional(), // {value, label}
      fileItems: z.array(z.any()).optional(), // {id, url, name, file}
      fileDeletedIds: z.array(z.number()).optional(),
      parentId: z.number().optional(),
      send_to_all_stores: z.number().optional(),
      send_to_all_groups: z.number().optional(),
      send_to_all_users: z.number().optional(),
    })
    .superRefine((data, ctx) => {
      // Schema này dùng chung cho các Role khác nhau khi tạo Notification
      // Theo Role sẽ có các quy tắc khác nhau để chọn target (stores, groups, members)

      // Role case: company admin & head office user
      // Send to: all store groups & all stores (only myself)
      if (role === RoleEnum.COMPANY_ADMIN) {
        const send_to_all_stores = data.send_to_all_stores;
        const send_to_all_groups = data.send_to_all_groups;
        const stores = data.stores || [];
        const groups = data.groups || [];

        if (
          send_to_all_groups === SendToAllEnum.OPTIONAL &&
          groups.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "この項目は必須です。",
            path: ["groups"],
          });
        }

        if (
          send_to_all_stores === SendToAllEnum.OPTIONAL &&
          stores.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "この項目は必須です。",
            path: ["stores"],
          });
        }

        const isNoTargetSelected =
          !stores.length &&
          !groups.length &&
          send_to_all_groups === SendToAllEnum.NONE &&
          send_to_all_stores === SendToAllEnum.NONE;
        if (isNoTargetSelected) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "通知を送信するには、少なくとも1つの対象を選択する必要があります！",
            path: ["global"], // hoặc [] nếu muốn global error
          });
        }
      }

      // Role case 2: Store manager
      // Send to: all members in my store
    });

const schema = getNotificationSchema(RoleEnum.COMPANY_ADMIN); // Default export schema for type only
export type NewNotificationSchemaType = z.infer<typeof schema>;
export { getNotificationSchema };
